/**
 * AnonymousIdentityInitializer Component
 * 
 * Initializes anonymous user identity on app load.
 * Creates an AnonymousProfile for every visitor.
 * 
 * Must be mounted inside AuthProvider but as a global component.
 * 
 * IMPORTANT: Uses the same fingerprint as tracking.ts (FingerprintJS)
 * to ensure consistency across the system.
 * 
 * AUTO-DETECTION FLOW:
 * When a new user lands, the backend detects their country by IP and returns:
 * - languageCode: Based on country (Spanish for LATAM, English otherwise)
 * - storeId: Based on country if we have a store, null for Global
 *   (Currency is now derived from store, not stored separately)
 * 
 * This component applies the detected language and stores the store preference
 * in localStorage for use by the MainNavigation dropdowns.
 */

import { useEffect, useRef } from 'react';
import { getOrCreateClientId, getLastKnownUserId } from '@/core/identity/identity';
import { anonymousIdentify as identify } from '@/core/api/generated/anonymous/anonymous';
import { getAttributionData } from '@/core/tracking/tracking';
import i18n from '@/core/i18n/i18n';

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

export function AnonymousIdentityInitializer() {
  const identifiedRef = useRef(false);

  useEffect(() => {
    // Only identify once per session
    if (identifiedRef.current) {
      return;
    }

    const doIdentify = async () => {
      try {
        identifiedRef.current = true;
        
        // Use getAttributionData from tracking.ts which:
        // 1. Waits for FingerprintJS to initialize
        // 2. Gets all UTMs, click IDs, referral code, etc.
        const attribution = await getAttributionData();
        const clientId = getOrCreateClientId();
        const lastKnownUserId = getLastKnownUserId();
        
        // Map attribution fields to identify API format
        const fingerprint = attribution.deviceFingerprint;

        console.log('[AnonymousIdentity] Identifying user...', { 
          clientId, 
          fingerprint,
          referralCode: attribution.referralCode,
          utmSource: attribution.signupUtmSource,
        });

        const result = await identify({
          clientId,
          fingerprint,
          knownUserId: lastKnownUserId,
          // UTMs
          utmSource: attribution.signupUtmSource,
          utmMedium: attribution.signupUtmMedium,
          utmCampaign: attribution.signupUtmCampaign,
          utmTerm: attribution.signupUtmTerm,
          utmContent: attribution.signupUtmContent,
          // Referral
          referralCode: attribution.referralCode,
          // Landing page and referrer
          landingPage: attribution.signupLandingPage,
          originalReferrer: attribution.signupReferrer,
          // Facebook cookies for Meta matching
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc'),
        });

        console.log('[AnonymousIdentity] ✅ Identified:', {
          profileId: result.profileId,
          clientId: result.clientId,
          isNewProfile: result.isNewProfile,
          knownUserId: result.knownUserId,
          detectedPreferences: result.detectedPreferences,
        });

        // If server returns a different clientId, restore it
        if (result.shouldRestoreClientId && result.clientId !== clientId) {
          localStorage.setItem('anon_client_id', result.clientId);
          console.log('[AnonymousIdentity] Restored clientId from server:', result.clientId);
        }

        // Store known user hint for "Welcome back" UI
        if (result.knownUserId) {
          console.log('[AnonymousIdentity] Known user detected (not logged in):', result.knownUserId);
        }

        // Apply detected preferences for new profiles
        if (result.detectedPreferences) {
          const prefs = result.detectedPreferences;
          
          // Apply language if detected and user hasn't manually set one
          // Check existence, not truthiness (user might have set empty preference)
          const userSetLanguage = localStorage.getItem('user_language_preference');
          if (prefs.languageCode && userSetLanguage === null) {
            const currentLang = i18n.language?.split('-')[0];
            if (currentLang !== prefs.languageCode) {
              console.log('[AnonymousIdentity] Applying detected language:', prefs.languageCode);
              i18n.changeLanguage(prefs.languageCode);
            }
          }

          // Store store preference for use by MainNavigation
          // Currency is now derived from store, not stored separately
          // Only if user hasn't manually set one (check existence, not truthiness)
          if (prefs.storeId) {
            const userSetStore = localStorage.getItem('user_store_preference');
            if (userSetStore === null) {
              localStorage.setItem('detected_store_id', prefs.storeId);
              console.log('[AnonymousIdentity] Stored detected store:', prefs.storeId);
            }
          }

          // Store detected country for reference
          if (prefs.countryCode) {
            localStorage.setItem('detected_country_code', prefs.countryCode);
          }
        }
      } catch (error) {
        console.error('[AnonymousIdentity] ❌ Failed to identify:', error);
        // Don't block the app, just log the error
      }
    };

    doIdentify();
  }, []);

  return null;
}

